import { useCallback, useEffect, useRef, useState } from 'react';
import type {
    SimulationGraph,
    SimulationResultPayload,
    SimulationRunConfig,
    SimulationWorkerMessage
} from '../../models/simulation';
import { useSimulationStore } from '../../store/simulationStore';

type RunnerStatus = 'idle' | 'running';

export const useSimulationRunner = () => {
    const workerRef = useRef<Worker | null>(null);
    const [status, setStatus] = useState<RunnerStatus>('idle');
    const [error, setError] = useState<string | null>(null);
    const setPartialResult = useSimulationStore((state) => state.setPartialResult);
    const setRunProgress = useSimulationStore((state) => state.setRunProgress);

    useEffect(() => {
        return () => {
            workerRef.current?.terminate();
            workerRef.current = null;
        };
    }, []);

    const ensureWorker = () => {
        if (!workerRef.current) {
            workerRef.current = new Worker(
                new URL('../../workers/simulationWorker.ts', import.meta.url),
                { type: 'module' }
            );
        }
        return workerRef.current;
    };

    const run = useCallback(
        async (
            graph: SimulationGraph, 
            runConfig: SimulationRunConfig,
            onProgress?: (completedTrials: number, totalTrials: number, partialResults: Record<string, any>) => void
        ): Promise<SimulationResultPayload> => {
            setStatus('running');
            setError(null);

            const worker = ensureWorker();

            return new Promise<SimulationResultPayload>((resolve, reject) => {
                const handleMessage = (event: MessageEvent<SimulationWorkerMessage>) => {
                    const message = event.data;
                    
                    // Handle different message types
                    if (message.type === 'progress') {
                        if (message.partialResults && Object.keys(message.partialResults).length > 0) {
                            setPartialResult(
                                message.partialResults,
                                message.completedTrials,
                                message.totalTrials
                            );
                        } else {
                            setRunProgress(message.completedTrials, message.totalTrials);
                        }
                        // Also call optional progress callback
                        if (onProgress) {
                            onProgress(message.completedTrials, message.totalTrials, message.partialResults ?? {});
                        }
                    } else if (message.type === 'complete' && message.ok) {
                        // Final completion - clean up and resolve
                        worker.removeEventListener('message', handleMessage);
                        worker.removeEventListener('error', handleError);
                        setStatus('idle');
                        resolve(message.payload);
                    } else if (message.type === 'error' && !message.ok) {
                        // Error - clean up and reject
                        worker.removeEventListener('message', handleMessage);
                        worker.removeEventListener('error', handleError);
                        const issue = message.error ?? 'Unknown worker error';
                        setStatus('idle');
                        setError(issue);
                        const err = new Error(issue);
                        if (message.nodeId) {
                            (err as any).nodeId = message.nodeId;
                        }
                        if (message.nodeName) {
                            (err as any).nodeName = message.nodeName;
                        }
                        reject(err);
                    }
                };

                const handleError = (event: ErrorEvent) => {
                    worker.removeEventListener('message', handleMessage);
                    worker.removeEventListener('error', handleError);
                    setStatus('idle');
                    const issue = event.message || 'Worker error';
                    setError(issue);
                    reject(new Error(issue));
                };

                worker.addEventListener('message', handleMessage);
                worker.addEventListener('error', handleError);
                worker.postMessage({ graph, runConfig });
            });
        },
        [setPartialResult, setRunProgress]
    );

    return {
        run,
        status,
        error,
        resetError: () => setError(null)
    };
};
